/**
 * Whether a sideways scroller still hides columns past its right edge, so the
 * Box score table can draw an edge fade until the fan reaches the end
 * (ISSUE-1166 R3).
 */
import { type RefObject, useEffect, useState } from "react";

/** Subpixel slack: a scroller within 1px of its end counts as at the end. */
export const SCROLL_END_TOLERANCE_PX = 1;

export function resolveIsScrollEndClipped(input: {
  scrollLeft: number;
  scrollWidth: number;
  clientWidth: number;
}): boolean {
  return (
    input.scrollWidth - input.clientWidth - input.scrollLeft >
    SCROLL_END_TOLERANCE_PX
  );
}

export function useIsScrollEndClipped(
  ref: RefObject<HTMLElement | null>,
): boolean {
  const [isClipped, setIsClipped] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (el === null) {
      return;
    }
    const update = (): void => {
      setIsClipped(
        resolveIsScrollEndClipped({
          scrollLeft: el.scrollLeft,
          scrollWidth: el.scrollWidth,
          clientWidth: el.clientWidth,
        }),
      );
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(el);
    if (el.firstElementChild !== null) {
      observer.observe(el.firstElementChild);
    }
    return () => {
      el.removeEventListener("scroll", update);
      observer.disconnect();
    };
  }, [ref]);

  return isClipped;
}
